import { useEffect, useRef } from 'react';
import { TAU, rand, canvasOf, makeGlow, makeNoise2D } from './meadow/sprites';

// Cloud Sky: light mode background for the Sky of Thoughts (v2).
// A clear blue sky framed by soft, sunlit cumulus clouds that drift slowly in
// layers. Thin high wisps, a warm sun glow and the odd bird flying past. The
// middle of the sky stays open, so the floating thoughts are easy to read.

type Cloud = { x: number; y: number; w: number; v: number; a: number; sprite: HTMLCanvasElement; depth: number };
type Wisp = { x: number; y: number; w: number; h: number; v: number; a: number; rot: number };
type Bird = { x: number; y: number; v: number; s: number; phase: number };

const clamp01 = (v: number) => Math.max(0, Math.min(1, v));
const smooth = (a: number, b: number, v: number) => { const k = clamp01((v - a) / (b - a)); return k * k * (3 - 2 * k); };

// A cumulus cloud grown from fractal noise inside a soft dome shape, so every
// cloud is billowy and different. Light comes from above: parts with more
// cloud between them and the sun turn a soft blue-grey, edges catch a warm rim.
// Drawn small, then scaled up on screen, which keeps it soft and dreamy.
function makeCumulus(): HTMLCanvasElement {
  const CW = 360, CH = 200, base = 150;
  const fbm = makeNoise2D();
  const nb = 3 + (Math.random() * 3 | 0);
  const blobs = Array.from({ length: nb }, (_, i) => {
    const u = (i + 0.5) / nb;
    const big = Math.sin(u * Math.PI);
    return { x: 40 + u * 280 + rand(-15, 15), y: base - 20 - big * rand(30, 55), rx: rand(55, 80) * (0.7 + big * 0.5), ry: rand(40, 60) * (0.6 + big * 0.6) };
  });
  const sx = rand(0, 100), sy = rand(0, 100);
  const dens = new Float32Array(CW * CH);
  for (let y = 0; y < CH; y++) {
    for (let x = 0; x < CW; x++) {
      let m = 0;
      for (const b of blobs) {
        const dx = (x - b.x) / b.rx, dy = (y - b.y) / b.ry;
        m = Math.max(m, 1 - (dx * dx + dy * dy));
      }
      m *= 1 - smooth(base - 12, base + 8, y); // flat-ish base
      const n = fbm(x / 38 + sx, y / 38 + sy);
      // Noise only shapes the cloud near its body, and fades to nothing well
      // before the sprite edges, so there are no stray specks or hard boxes.
      const edge = Math.min(x, CW - 1 - x, y, CH - 1 - y) / 18;
      dens[y * CW + x] = (m * 0.9 + (n - 0.5) * 0.9) * smooth(-0.25, 0.1, m) * clamp01(edge);
    }
  }
  const img = new ImageData(CW, CH);
  const d = img.data;
  for (let y = 0; y < CH; y++) {
    for (let x = 0; x < CW; x++) {
      const i = y * CW + x;
      const a = smooth(0.08, 0.42, dens[i]);
      if (a <= 0) continue;
      // Cloud density a little toward the sun (up and to the left).
      const lx = Math.max(0, x - 4), ly = Math.max(0, y - 9);
      const occl = clamp01((dens[ly * CW + lx] - dens[i]) * 2.2 + 0.25);
      const low = clamp01((y - (base - 70)) / 80);
      const shade = clamp01(occl * 0.75 + low * 0.45);
      const rim = (1 - smooth(0.08, 0.3, dens[i])) * (1 - low);
      d[i * 4] = Math.round(255 - shade * 78);
      d[i * 4 + 1] = Math.round(252 - shade * 62 - rim * 4);
      d[i * 4 + 2] = Math.round(246 - shade * 34 - rim * 16);
      d[i * 4 + 3] = Math.round(a * 255);
    }
  }
  const c = canvasOf(CW, CH);
  c.getContext('2d')!.putImageData(img, 0, 0);
  return c;
}

export default function CloudSky({ className = '' }: { className?: string }) {
  const ref = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    const canvas = ref.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d')!;
    const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const sprites = Array.from({ length: 5 }, makeCumulus);
    const sunGlow = makeGlow('255, 248, 232', 0.4);

    let W = 0, H = 0, dpr = 1, t = 0, raf = 0;
    let clouds: Cloud[] = [], wisps: Wisp[] = [], birds: Bird[] = [];
    const pointer = { x: 0, y: 0, last: -1e9 };
    const sky = document.createElement('canvas');

    const build = () => {
      dpr = Math.min(window.devicePixelRatio || 1, 1.5);
      W = canvas.clientWidth || window.innerWidth;
      H = canvas.clientHeight || window.innerHeight;
      canvas.width = W * dpr;
      canvas.height = H * dpr;
      const mobile = W < 768;

      sky.width = canvas.width;
      sky.height = canvas.height;
      const s = sky.getContext('2d')!;
      s.setTransform(dpr, 0, 0, dpr, 0, 0);
      const g = s.createLinearGradient(0, 0, 0, H);
      g.addColorStop(0, '#3f8fdc');
      g.addColorStop(0.45, '#6aaeea');
      g.addColorStop(0.8, '#a6d0f3');
      g.addColorStop(1, '#d4e8f8');
      s.fillStyle = g;
      s.fillRect(0, 0, W, H);

      // Clouds frame the top and bottom, like the old photo; the centre stays clear.
      const band = (n: number, y: [number, number], w: [number, number], depth: number, a: [number, number]) =>
        Array.from({ length: n }, () => ({
          x: rand(-0.3, 1.1) * W, y: rand(...y) * H, w: rand(...w) * W * (mobile ? 1.8 : 1),
          v: (0.05 + depth * 0.12) * rand(0.8, 1.2), a: rand(...a),
          sprite: sprites[Math.random() * sprites.length | 0], depth,
        }));
      clouds = [
        ...band(mobile ? 4 : 7, [0.3, 0.62], [0.1, 0.2], 0.2, [0.5, 0.75]),
        ...band(mobile ? 2 : 4, [-0.06, 0.1], [0.28, 0.42], 0.6, [0.9, 1]),
        ...band(mobile ? 2 : 4, [0.76, 0.9], [0.28, 0.44], 0.8, [0.9, 1]),
        ...band(mobile ? 2 : 3, [0.98, 1.08], [0.5, 0.75], 1, [1, 1]),
      ].sort((a, b) => a.depth - b.depth);
      wisps = Array.from({ length: mobile ? 4 : 7 }, () => ({
        x: Math.random() * W, y: rand(0.08, 0.45) * H, w: rand(0.15, 0.35) * W, h: rand(6, 16),
        v: rand(0.03, 0.07), a: rand(0.18, 0.35), rot: rand(-0.12, 0.05),
      }));
      birds = [];
    };

    const frame = () => {
      if (!sky.width || !sky.height) return;
      t += 1 / 60;
      const active = t - pointer.last < 4;
      const px = active ? pointer.x / W - 0.5 : 0;
      const py = active ? pointer.y / H - 0.5 : 0;

      ctx.setTransform(1, 0, 0, 1, 0, 0);
      ctx.drawImage(sky, 0, 0);
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);

      // Warm sun glow in the upper right.
      ctx.globalAlpha = 0.5 + 0.05 * Math.sin(t * 0.3);
      const gr = Math.max(W, H) * 0.5;
      ctx.drawImage(sunGlow, W * 0.82 - gr, -H * 0.1 - gr, gr * 2, gr * 2);
      ctx.globalAlpha = 1;

      // High, thin wisps.
      for (const w of wisps) {
        w.x += w.v;
        if (w.x - w.w > W) w.x = -w.w;
        ctx.save();
        ctx.translate(w.x - px * 6, w.y);
        ctx.rotate(w.rot);
        const g = ctx.createRadialGradient(0, 0, 0, 0, 0, w.w / 2);
        g.addColorStop(0, `rgba(255, 255, 255, ${w.a})`);
        g.addColorStop(1, 'rgba(255, 255, 255, 0)');
        ctx.fillStyle = g;
        ctx.scale(1, w.h / w.w);
        ctx.beginPath();
        ctx.arc(0, 0, w.w / 2, 0, TAU);
        ctx.fill();
        ctx.restore();
      }

      // A bird or a small flock, now and then.
      if (birds.length === 0 && Math.random() < 0.002) {
        const n = 1 + (Math.random() * 4 | 0), y0 = rand(0.15, 0.5) * H, dir = Math.random() < 0.5 ? 1 : -1;
        for (let i = 0; i < n; i++) {
          birds.push({ x: dir > 0 ? -30 - i * 26 : W + 30 + i * 26, y: y0 + rand(-18, 18), v: dir * rand(0.9, 1.3), s: rand(4, 7), phase: Math.random() * TAU });
        }
      }
      ctx.strokeStyle = 'rgba(40, 60, 90, 0.55)';
      ctx.lineWidth = 1.2;
      ctx.lineCap = 'round';
      birds = birds.filter(b => b.x > -80 && b.x < W + 80);
      for (const b of birds) {
        b.x += b.v;
        b.y += Math.sin(t * 1.5 + b.phase) * 0.15;
        const f = Math.sin(t * 9 + b.phase) * b.s * 0.6;
        ctx.beginPath();
        ctx.moveTo(b.x - b.s, b.y - f);
        ctx.quadraticCurveTo(b.x - b.s * 0.4, b.y - b.s * 0.2, b.x, b.y);
        ctx.quadraticCurveTo(b.x + b.s * 0.4, b.y - b.s * 0.2, b.x + b.s, b.y - f);
        ctx.stroke();
      }

      // Cumulus, far to near, with parallax on pointer move.
      for (const c of clouds) {
        c.x += c.v;
        if (c.x > W + 20) c.x = -c.w - 20;
        const h = c.w * (200 / 360);
        // A slow swell and bob, so the clouds feel alive without distracting.
        const swell = 1 + Math.sin(t * 0.12 + c.depth * 7 + c.x * 0.001) * 0.025;
        const cw = c.w * swell, ch = h * (2 - swell);
        ctx.globalAlpha = c.a;
        ctx.imageSmoothingQuality = 'high';
        ctx.drawImage(c.sprite, c.x - px * 30 * c.depth - (cw - c.w) / 2, c.y - ch * 0.75 - py * 12 * c.depth + Math.sin(t * 0.2 + c.depth * 5) * 3, cw, ch);
      }
      ctx.globalAlpha = 1;
    };

    const loop = () => {
      frame();
      raf = requestAnimationFrame(loop);
    };
    const start = () => { if (!raf && !reduced) raf = requestAnimationFrame(loop); };
    const stop = () => { cancelAnimationFrame(raf); raf = 0; };
    const onMove = (e: PointerEvent) => {
      const r = canvas.getBoundingClientRect();
      pointer.x = e.clientX - r.left;
      pointer.y = e.clientY - r.top;
      pointer.last = t;
    };
    const onVis = () => (document.hidden ? stop() : start());
    let resizeTimer = 0;
    const onResize = () => {
      clearTimeout(resizeTimer);
      resizeTimer = window.setTimeout(() => { build(); frame(); }, 150);
    };

    build();
    frame();
    start();
    window.addEventListener('pointermove', onMove, { passive: true });
    window.addEventListener('resize', onResize);
    document.addEventListener('visibilitychange', onVis);
    return () => {
      stop();
      clearTimeout(resizeTimer);
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('resize', onResize);
      document.removeEventListener('visibilitychange', onVis);
    };
  }, []);

  return <canvas ref={ref} aria-hidden="true" className={`block w-full h-full pointer-events-none ${className}`} />;
}
